import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ArrowLeft, ArrowUp, AtSign, Hash, MessageCircle, Plus, Search, Send, Users, X } from "lucide-react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/AuthContext";
import { useIsMobile } from "@/hooks/useBreakpoint";
import useOverlayScrollLock from "@/hooks/useOverlayScrollLock";
import api, { formatApiError } from "@/api";
import ChatPushSettings from "./ChatPushSettings";
import "./chat.css";

const team = { id: "team", kind: "team", title: "Team room", participants: [] };

function initials(name = "") {
  return name.split(/\s+/).filter(Boolean).slice(0, 2).map(part => part[0]).join("").toUpperCase() || "?";
}

function shortTime(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleTimeString("en-IN", { timeZone: "Asia/Kolkata", hour: "numeric", minute: "2-digit" });
}

function dateLabel(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const today = new Date();
  const istDate = new Intl.DateTimeFormat("en-IN", {
    timeZone: "Asia/Kolkata", year: "numeric", month: "2-digit", day: "2-digit",
  });
  if (istDate.format(date) === istDate.format(today)) return shortTime(value);
  return date.toLocaleDateString("en-IN", { timeZone: "Asia/Kolkata", month: "short", day: "numeric" });
}

function conversationName(conversation, userId) {
  if (conversation.id === "team" || conversation.kind === "team") return "Team room";
  if (conversation.title) return conversation.title;
  const other = (conversation.participants || []).find(person => String(person.id) !== String(userId));
  return other?.name || "Private conversation";
}

function conversationPhoto(conversation, userId, staffById) {
  const other = (conversation.participants || []).find(person => String(person.id) !== String(userId));
  return other?.profile_photo_url || staffById.get(String(other?.id))?.profile_photo_url || null;
}

function ChatView({ compact = false, onClose, embeddedMobile = false, mobilePage = false, initialConversationId = null }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [conversations, setConversations] = useState([]);
  const [staff, setStaff] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [drafts, setDrafts] = useState({});
  const [selectedMentions, setSelectedMentions] = useState({});
  const [query, setQuery] = useState("");
  const [loadingList, setLoadingList] = useState(true);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [listError, setListError] = useState("");
  const [messageError, setMessageError] = useState("");
  const [sending, setSending] = useState(false);
  const [staffPicker, setStaffPicker] = useState(false);
  const [mentionOpen, setMentionOpen] = useState(false);
  const [mentionQuery, setMentionQuery] = useState("");
  const [mentionIndex, setMentionIndex] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [listReady, setListReady] = useState(false);
  const [unavailableConversation, setUnavailableConversation] = useState(false);
  const listRef = useRef(null);
  const textareaRef = useRef(null);
  const newPrivateRef = useRef(null);
  const scrollAtBottom = useRef(true);
  const readMarkers = useRef({});
  const activeIdRef = useRef(activeId);
  const initializedListRef = useRef(false);
  const resolvedQueryRef = useRef(null);
  const mobileBehavior = embeddedMobile || mobilePage;
  const active = conversations.find(item => String(item.id) === String(activeId)) || null;
  const staffById = useMemo(() => new Map(staff.map(person => [String(person.id), person])), [staff]);
  activeIdRef.current = activeId;

  const loadList = useCallback(async (silent = false) => {
    try {
      if (!silent) setLoadingList(true);
      const [conversationResponse, staffResponse] = await Promise.all([
        api.get("/chat/conversations"),
        api.get("/chat/users"),
      ]);
      const items = conversationResponse.data?.items || [];
      const withTeam = items.some(item => String(item.id) === "team")
        ? items
        : [team, ...items];
      setConversations(withTeam);
      setStaff(staffResponse.data?.items || []);
      setListError("");
      const isFirstList = !initializedListRef.current;
      initializedListRef.current = true;
      setListReady(true);
      setActiveId(current => {
        if (current && withTeam.some(item => String(item.id) === String(current))) return current;
        if (!isFirstList || initialConversationId) return current;
        if (mobileBehavior) return null;
        return withTeam.some(item => String(item.id) === "team") ? "team" : (withTeam[0]?.id ?? null);
      });
    } catch (error) {
      if (!silent) setListError(formatApiError(error));
    } finally {
      if (!silent) setLoadingList(false);
    }
  }, [initialConversationId, mobileBehavior]);

  const loadMessages = useCallback(async (id, silent = false) => {
    if (!id || (mobileBehavior && String(activeIdRef.current) !== String(id))) return;
    const pageThreadCovered = () => mobilePage && Boolean(document.querySelector(".vb-chat-overlay.is-mobile"));
    if (pageThreadCovered()) return;
    try {
      if (!silent) setLoadingMessages(true);
      const response = await api.get(`/chat/conversations/${encodeURIComponent(id)}/messages`, { params: { limit: 50 } });
      if (pageThreadCovered()) return;
      const loaded = response.data?.items || [];
      setMessages(current => {
        if (String(activeIdRef.current) !== String(id)) return current;
        return silent ? mergeMessages(current, loaded) : loaded;
      });
      if (String(activeIdRef.current) === String(id)) {
        setHasMore(Boolean(response.data?.has_more));
        setMessageError("");
      }
      const last = loaded[loaded.length - 1];
      if (String(activeIdRef.current) === String(id)
        && last?.id != null
        && String(readMarkers.current[id]) !== String(last.id)) {
        await api.post(`/chat/conversations/${encodeURIComponent(id)}/read`, { message_id: last.id });
        readMarkers.current[id] = last.id;
        setConversations(current => current.map(item => String(item.id) === String(id) ? { ...item, unread_count: 0 } : item));
      }
    } catch (error) {
      if (!silent && String(activeIdRef.current) === String(id)) setMessageError(formatApiError(error));
    } finally {
      if (!silent && String(activeIdRef.current) === String(id)) setLoadingMessages(false);
    }
  }, [mobileBehavior, mobilePage]);

  const loadOlder = async () => {
    if (!activeId || !hasMore || loadingOlder || !messages[0]?.id) return;
    const viewport = listRef.current;
    const oldHeight = viewport?.scrollHeight || 0;
    const oldTop = viewport?.scrollTop || 0;
    setLoadingOlder(true);
    try {
      const response = await api.get(`/chat/conversations/${encodeURIComponent(activeId)}/messages`, {
        params: { limit: 50, before: messages[0].id },
      });
      setMessages(current => mergeMessages(current, response.data?.items || []));
      setHasMore(Boolean(response.data?.has_more));
      requestAnimationFrame(() => {
        if (viewport) viewport.scrollTop = oldTop + viewport.scrollHeight - oldHeight;
      });
    } catch (error) {
      setMessageError(formatApiError(error));
    } finally {
      setLoadingOlder(false);
    }
  };

  useEffect(() => { loadList(); }, [loadList]);
  useEffect(() => {
    if (!listReady) return;
    if (!initialConversationId) {
      resolvedQueryRef.current = null;
      setUnavailableConversation(false);
      return;
    }
    const requestedId = String(initialConversationId);
    if (resolvedQueryRef.current === requestedId) return;
    const found = conversations.find(item => String(item.id) === requestedId);
    if (found) {
      setActiveId(found.id);
      setUnavailableConversation(false);
      resolvedQueryRef.current = requestedId;
    } else {
      setActiveId(null);
      setUnavailableConversation(true);
      resolvedQueryRef.current = requestedId;
    }
  }, [listReady, initialConversationId, conversations]);
  useEffect(() => {
    if (!activeId) return;
    setMessages([]);
    setHasMore(false);
    setMessageError("");
    scrollAtBottom.current = true;
    loadMessages(activeId);
  }, [activeId, loadMessages]);

  useEffect(() => {
    let socket;
    let retryTimer;
    let refreshTimer;
    let presenceTimer;
    let pushEndpoint = null;
    let closed = false;
    let failures = 0;
    const refresh = () => {
      if (document.hidden) return;
      loadList(true);
      const visibleId = activeIdRef.current;
      if (visibleId) loadMessages(visibleId, true);
    };
    const scheduleRefresh = () => {
      if (refreshTimer) clearInterval(refreshTimer);
      refreshTimer = null;
      if (closed || document.hidden) return;
      const interval = socket?.readyState === WebSocket.OPEN ? 60000 : 18000;
      refreshTimer = setInterval(refresh, interval);
    };
    const connect = () => {
      if (closed) return;
      try {
        const scheme = window.location.protocol === "https:" ? "wss" : "ws";
        socket = new WebSocket(`${scheme}://${window.location.host}/api/chat/ws`);
        socket.onopen = () => {
          failures = 0;
          reportPresence();
          refresh();
          scheduleRefresh();
        };
        socket.onmessage = event => {
          try {
            const payload = JSON.parse(event.data);
            if (payload?.type === "message") refresh();
          } catch (_) {}
        };
        socket.onerror = () => {};
        socket.onclose = () => {
          if (closed) return;
          failures += 1;
          scheduleRefresh();
          retryTimer = setTimeout(connect, [1000, 3000, 8000][Math.min(failures - 1, 2)]);
        };
      } catch (_) { scheduleRefresh(); }
    };
    const sendPresence = () => {
      if (socket?.readyState === WebSocket.OPEN) {
        socket.send(JSON.stringify({
          type: "presence", visible: !document.hidden, endpoint: pushEndpoint,
        }));
      }
    };
    const reportPresence = () => {
      sendPresence();
      if (document.hidden) return;
      // The endpoint identifies this browser, not the whole account. If chat
      // is open on a computer, another device must still receive its push.
      if (!navigator.serviceWorker) return;
      navigator.serviceWorker.ready
        .then(reg => reg.pushManager?.getSubscription())
        .then(sub => {
          if (closed) return;
          const next = sub?.endpoint || null;
          if (next !== pushEndpoint) {
            pushEndpoint = next;
            sendPresence();
          }
        })
        .catch(() => {});
    };
    const onVisibilityChange = () => {
      reportPresence();
      if (!document.hidden) refresh();
      scheduleRefresh();
    };
    connect();
    scheduleRefresh();
    document.addEventListener("visibilitychange", onVisibilityChange);
    presenceTimer = setInterval(reportPresence, 60000);
    return () => {
      closed = true;
      document.removeEventListener("visibilitychange", onVisibilityChange);
      clearInterval(presenceTimer);
      socket?.close();
      clearTimeout(retryTimer);
      if (refreshTimer) clearInterval(refreshTimer);
    };
  }, [loadList, loadMessages]);

  useEffect(() => {
    if (scrollAtBottom.current && listRef.current) {
      listRef.current.scrollTop = listRef.current.scrollHeight;
    }
  }, [messages]);

  const filteredConversations = useMemo(() => {
    const term = query.trim().toLowerCase();
    return conversations.filter(item => !term || conversationName(item, user?.id).toLowerCase().includes(term)
      || item.last_message?.body?.toLowerCase().includes(term));
  }, [conversations, query, user?.id]);

  const createDirect = async person => {
    try {
      const response = await api.post("/chat/conversations/direct", { user_id: person.id });
      const created = response.data;
      setConversations(current => [created, ...current.filter(item => String(item.id) !== String(created.id))]);
      setActiveId(created.id);
      setStaffPicker(false);
      setQuery("");
    } catch (error) {
      setListError(formatApiError(error));
    }
  };

  const changeDraft = value => {
    const caret = textareaRef.current?.selectionStart ?? value.length;
    setDrafts(current => ({ ...current, [activeId]: value }));
    setSelectedMentions(current => ({
      ...current,
      [activeId]: (current[activeId] || []).filter(person => value.includes(`@${person.name}`)),
    }));
    const preceding = value.slice(0, caret);
    const match = preceding.match(/(?:^|\s)@([^\s@]*)$/);
    setMentionOpen(Boolean(match));
    setMentionQuery(match?.[1] || "");
    setMentionIndex(0);
  };

  const mentionCandidates = staff.filter(person => String(person.id) !== String(user?.id)
    && (active?.id === "team" || active?.kind === "team"
      || (active?.participants || []).some(participant => String(participant.id) === String(person.id)))
    && person.name.toLowerCase().includes(mentionQuery.toLowerCase())).slice(0, 6);

  const chooseMention = person => {
    const input = textareaRef.current;
    const value = drafts[activeId] || "";
    const caret = input?.selectionStart ?? value.length;
    const before = value.slice(0, caret).replace(/(?:^|\s)@[^\s@]*$/, match => match.startsWith(" ") ? " " : "");
    const after = value.slice(caret);
    const next = `${before}@${person.name} ${after}`;
    setDrafts(current => ({ ...current, [activeId]: next }));
    setSelectedMentions(current => {
      const prior = current[activeId] || [];
      return { ...current, [activeId]: prior.some(item => String(item.id) === String(person.id)) ? prior : [...prior, person] };
    });
    setMentionOpen(false);
    requestAnimationFrame(() => {
      input?.focus();
      const position = before.length + person.name.length + 2;
      input?.setSelectionRange(position, position);
    });
  };

  const sendMessage = async event => {
    event?.preventDefault();
    const body = (drafts[activeId] || "").trim();
    if (!body || !activeId || sending) return;
    setSending(true);
    setMessageError("");
    try {
      const response = await api.post(`/chat/conversations/${encodeURIComponent(activeId)}/messages`, {
        body,
        mention_ids: (selectedMentions[activeId] || [])
          .filter(person => active?.id === "team" || active?.kind === "team"
            || (active?.participants || []).some(participant => String(participant.id) === String(person.id)))
          .map(person => person.id),
      });
      const sent = response.data;
      if (String(activeIdRef.current) === String(activeId)) setMessages(current => mergeMessages(current, [sent]));
      setDrafts(current => ({ ...current, [activeId]: "" }));
      setSelectedMentions(current => ({ ...current, [activeId]: [] }));
      setConversations(current => current.map(item => String(item.id) === String(activeId)
        ? { ...item, last_message: { id: sent.id, body: sent.body, sender_id: sent.sender_id, created_at: sent.created_at } }
        : item));
      if (String(activeIdRef.current) === String(activeId)) {
        scrollAtBottom.current = true;
        requestAnimationFrame(() => { if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight; });
      }
    } catch (error) {
      if (String(activeIdRef.current) === String(activeId)) setMessageError(formatApiError(error));
    } finally {
      setSending(false);
    }
  };

  const openFull = () => { onClose?.(); navigate("/chat"); };
  const showConversation = compact ? Boolean(activeId) : (!mobileBehavior || Boolean(activeId));

  return (
    <section className={`vb-chat ${compact ? "vb-chat--compact" : "vb-chat--full"} ${embeddedMobile ? "vb-chat--mobile" : ""} ${mobilePage ? "is-mobile-page" : ""} ${mobileBehavior && activeId ? "is-thread-open" : ""}`} aria-label="Staff chat">
      <div className="vb-chat__sidebar">
        <header className="vb-chat__list-head">
          <div className="vb-chat__brand"><span className="vb-chat__brand-mark"><MessageCircle size={16} /></span><div><strong>Messages</strong><small>VianBills staff</small></div></div>
          <div className="vb-chat__head-actions">
            {compact && <button className="vb-chat__icon-btn" onClick={openFull} aria-label="Open full inbox" title="Open full inbox"><ArrowUp size={16} /></button>}
            {onClose && <button className="vb-chat__icon-btn" onClick={onClose} aria-label="Close chat"><X size={17} /></button>}
          </div>
        </header>
        <div className="vb-chat__search"><Search size={15} /><input value={query} onChange={event => setQuery(event.target.value)} placeholder="Find a conversation" aria-label="Find a conversation" /></div>
        <button ref={newPrivateRef} className="vb-chat__new" onClick={() => setStaffPicker(value => !value)} aria-expanded={staffPicker}><Plus size={15} /> New private message</button>
        {staffPicker && <div className="vb-chat__staff-picker" onKeyDown={event => {
          if (event.key === "Escape") { event.stopPropagation(); setStaffPicker(false); newPrivateRef.current?.focus(); }
        }}>
          <div className="vb-chat__staff-picker-heading">
            <span>Choose a colleague</span>
            <button type="button" onClick={() => { setStaffPicker(false); newPrivateRef.current?.focus(); }}>Cancel</button>
          </div>
          <div className="vb-chat__staff-picker-list" role="list" aria-label="Staff members">
            {staff.filter(person => String(person.id) !== String(user?.id)).map(person => <button key={person.id} onClick={() => createDirect(person)}><Avatar name={person.name} photoUrl={person.profile_photo_url} /><span><b>{person.name}</b><small>{person.role || "Staff"}</small></span></button>)}
            {staff.filter(person => String(person.id) !== String(user?.id)).length === 0 && <p>No other staff available.</p>}
          </div>
        </div>}
        <div className="vb-chat__section-label"><span>CONVERSATIONS</span><span>{conversations.length}</span></div>
        <div className="vb-chat__conversation-list">
          {unavailableConversation && <div className="vb-chat__deep-link-error" role="alert"><strong>Conversation unavailable</strong><span>This conversation is not available to your account or no longer exists. Choose another conversation below.</span></div>}
          {loadingList && conversations.length === 0 && <ConversationSkeleton />}
          {listError && conversations.length === 0 && <div className="vb-chat__notice"><p>Messages could not be loaded.</p><button onClick={() => loadList()}>Try again</button></div>}
          {!loadingList && !listError && filteredConversations.length === 0 && <div className="vb-chat__list-empty"><MessageCircle size={20} /><span>No conversations yet</span></div>}
          {filteredConversations.map(item => <button key={item.id} className={`vb-chat__conversation ${String(activeId) === String(item.id) ? "is-active" : ""}`} onClick={() => { setUnavailableConversation(false); setActiveId(item.id); }}>
            {item.id === "team" || item.kind === "team" ? <span className="vb-chat__avatar vb-chat__avatar--team"><Users size={17} /></span> : <Avatar name={conversationName(item, user?.id)} photoUrl={conversationPhoto(item, user?.id, staffById)} />}
            <span className="vb-chat__conversation-copy"><span className="vb-chat__conversation-title">{conversationName(item, user?.id)}</span><span className="vb-chat__preview">{item.last_message?.body || (item.id === "team" ? "Shared space for the whole team" : "Start a private conversation")}</span></span>
            <span className="vb-chat__conversation-meta">{item.last_message?.created_at && <time>{dateLabel(item.last_message.created_at)}</time>}{Number(item.unread_count) > 0 && <b className="vb-chat__unread">{item.unread_count}</b>}</span>
          </button>)}
        </div>
        {!(mobileBehavior && activeId) && <ChatPushSettings />}
      </div>
      {showConversation && <main className="vb-chat__thread">
        {active ? <>
          <header className="vb-chat__thread-head">
            {mobileBehavior && <button className="vb-chat__icon-btn" onClick={() => setActiveId(null)} aria-label="Back to conversations"><ArrowLeft size={17} /></button>}
            {active.id === "team" || active.kind === "team" ? <span className="vb-chat__avatar vb-chat__avatar--team"><Users size={17} /></span> : <Avatar name={conversationName(active, user?.id)} photoUrl={conversationPhoto(active, user?.id, staffById)} />}
            <div className="vb-chat__thread-title"><strong>{conversationName(active, user?.id)}</strong><span>{active.id === "team" ? "Shared team conversation" : "Private message"}</span></div>
            {!compact && <span className="vb-chat__private"><span /> Private to participants</span>}
          </header>
          {mobileBehavior && <ChatPushSettings />}
          <div className="vb-chat__messages" ref={listRef} onScroll={event => { const el = event.currentTarget; scrollAtBottom.current = el.scrollHeight - el.scrollTop - el.clientHeight < 70; }}>
            {hasMore && <button className="vb-chat__load-older" onClick={loadOlder} disabled={loadingOlder}>{loadingOlder ? "Loading earlier messages" : "Load earlier messages"}</button>}
            {loadingMessages && messages.length === 0 && <MessageSkeleton />}
            {messageError && messages.length === 0 && <div className="vb-chat__notice"><p>Messages could not be loaded.</p><button onClick={() => loadMessages(activeId)}>Try again</button></div>}
            {!loadingMessages && !messageError && messages.length === 0 && <div className="vb-chat__empty"><span className="vb-chat__empty-symbol">{active.id === "team" ? <Hash size={20} /> : <MessageCircle size={20} />}</span><strong>{active.id === "team" ? "The team room" : "A private conversation"}</strong><p>Share a clear update with the people who need it.</p></div>}
            {messages.map((message, index) => <MessageBubble key={message.id} message={message} mine={String(message.sender_id) === String(user?.id)} previous={messages[index - 1]} photoUrl={staffById.get(String(message.sender_id))?.profile_photo_url} />)}
          </div>
          <form className="vb-chat__composer" onSubmit={sendMessage}>
            {mentionOpen && mentionCandidates.length > 0 && <div className="vb-chat__mention-menu" role="listbox" aria-label="Mention a staff member">{mentionCandidates.map((person, index) => <button type="button" key={person.id} role="option" aria-selected={index === mentionIndex} className={index === mentionIndex ? "is-highlighted" : ""} onMouseEnter={() => setMentionIndex(index)} onClick={() => chooseMention(person)}><Avatar name={person.name} photoUrl={person.profile_photo_url} /><span><b>{person.name}</b><small>{person.role || "Staff"}</small></span></button>)}</div>}
            <div className="vb-chat__compose-shell">
              <textarea ref={textareaRef} value={drafts[activeId] || ""} onChange={event => changeDraft(event.target.value)} onKeyDown={event => {
                if (mentionOpen && mentionCandidates.length && event.key === "ArrowDown") { event.preventDefault(); setMentionIndex(index => (index + 1) % mentionCandidates.length); }
                if (mentionOpen && mentionCandidates.length && event.key === "ArrowUp") { event.preventDefault(); setMentionIndex(index => (index - 1 + mentionCandidates.length) % mentionCandidates.length); }
                if (mentionOpen && mentionCandidates.length && event.key === "Enter" && !event.shiftKey) { event.preventDefault(); chooseMention(mentionCandidates[mentionIndex]); return; }
                if (event.key === "Escape") setMentionOpen(false);
                if (event.key === "Enter" && !event.shiftKey) { event.preventDefault(); sendMessage(); }
              }} placeholder={`Message ${active.id === "team" ? "the team" : conversationName(active, user?.id)}`} rows={2} aria-label="Write a message" />
              <div className="vb-chat__compose-tools"><span><AtSign size={13} /> Type @ to mention staff</span><button type="submit" disabled={sending || !(drafts[activeId] || "").trim()} aria-label="Send message">{sending ? <span className="vb-chat__send-pending" /> : <Send size={15} />}<span>{sending ? "Sending" : "Send"}</span></button></div>
            </div>
            {messageError && <div className="vb-chat__send-error" role="alert">{messageError}</div>}
            {(selectedMentions[activeId] || []).length > 0 && <div className="vb-chat__mention-chips">{selectedMentions[activeId].map(person => <span key={person.id}>@{person.name}<button type="button" onClick={() => setSelectedMentions(current => ({ ...current, [activeId]: current[activeId].filter(item => String(item.id) !== String(person.id)) }))} aria-label={`Remove ${person.name} mention`}><X size={11} /></button></span>)}</div>}
          </form>
        </> : <div className="vb-chat__mobile-prompt"><MessageCircle size={24} /><strong>Select a conversation</strong><span>Choose a thread to read and reply.</span></div>}
      </main>}
    </section>
  );
}

function mergeMessages(current, incoming) {
  const byId = new Map(current.map(message => [String(message.id), message]));
  incoming.forEach(message => byId.set(String(message.id), message));
  return Array.from(byId.values()).sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
}

function Avatar({ name, photoUrl }) {
  const [failedUrl, setFailedUrl] = useState(null);
  return <span className="vb-chat__avatar" aria-hidden="true">
    {photoUrl && failedUrl !== photoUrl
      ? <img src={photoUrl} alt="" onError={() => setFailedUrl(photoUrl)} />
      : initials(name)}
  </span>;
}

function MessageBubble({ message, mine, previous, photoUrl }) {
  const grouped = previous && String(previous.sender_id) === String(message.sender_id)
    && new Date(message.created_at) - new Date(previous.created_at) < 5 * 60 * 1000;
  return <article className={`vb-chat__message ${mine ? "is-mine" : ""} ${grouped ? "is-grouped" : ""}`}>
    {!mine && !grouped && <Avatar name={message.sender_name} photoUrl={photoUrl} />}
    {!mine && grouped && <span className="vb-chat__avatar-spacer" />}
    <div className="vb-chat__message-content">
      {!mine && !grouped && <div className="vb-chat__sender">{message.sender_name}</div>}
      <div className="vb-chat__bubble">{renderMentions(message.body, message.mentions)}</div>
      <time className="vb-chat__message-time">{shortTime(message.created_at)}</time>
    </div>
  </article>;
}

function renderMentions(body, mentions = []) {
  if (!mentions?.length) return body;
  const names = mentions.map(mention => mention.name).filter(Boolean).sort((a, b) => b.length - a.length);
  if (!names.length) return body;
  const matcher = new RegExp(`@(${names.map(name => name.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")).join("|")})(?=$|\\s|[.,!?;:])`, "g");
  const output = [];
  let cursor = 0;
  let match;
  while ((match = matcher.exec(String(body || "")))) {
    if (match.index > cursor) output.push(String(body).slice(cursor, match.index));
    output.push(<strong className="vb-chat__mention" key={`${match.index}-${match[1]}`}>{match[0]}</strong>);
    cursor = matcher.lastIndex;
  }
  if (cursor < String(body || "").length) output.push(String(body).slice(cursor));
  return output;
}

function ConversationSkeleton() {
  return <div className="vb-chat__skeletons" aria-label="Loading conversations">{[1, 2, 3].map(item => <span key={item}><i /><b /><em /></span>)}</div>;
}

function MessageSkeleton() {
  return <div className="vb-chat__message-skeleton" aria-label="Loading messages"><i /><b /><i /><b /></div>;
}

export function ChatLauncher() {
  const [open, setOpen] = useState(false);
  useOverlayScrollLock(open);
  const mobile = useIsMobile();
  const [unread, setUnread] = useState(0);
  const launcherRef = useRef(null);
  const dialogRef = useRef(null);
  useEffect(() => {
    let live = true;
    let interval;
    const refresh = () => {
      if (!live || document.hidden || open) return;
      api.get("/chat/unread")
        .then(response => { if (live) setUnread(Number(response.data?.count || 0)); })
        .catch(() => {});
    };
    const scheduleRefresh = () => {
      if (interval) clearInterval(interval);
      interval = null;
      if (!open && !document.hidden) interval = setInterval(refresh, 60000);
    };
    const onVisibilityChange = () => {
      if (!document.hidden) refresh();
      scheduleRefresh();
    };
    if (!open) refresh();
    scheduleRefresh();
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () => {
      live = false;
      document.removeEventListener("visibilitychange", onVisibilityChange);
      if (interval) clearInterval(interval);
    };
  }, [open]);
  useEffect(() => {
    if (!open) return;
    const onKeyDown = event => {
      if (event.key === "Escape") {
        setOpen(false);
        launcherRef.current?.focus();
      }
    };
    document.addEventListener("keydown", onKeyDown);
    requestAnimationFrame(() => dialogRef.current?.querySelector("button")?.focus());
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [open]);
  return <>
    <button ref={launcherRef} className="vb-chat-launcher" onClick={() => setOpen(true)} aria-label={`Open chat${unread ? `, ${unread} unread` : ""}`} aria-expanded={open} data-testid="topbar-chat">
      <MessageCircle size={17} />
      {unread > 0 && <span className="vb-chat-launcher__badge">{unread > 9 ? "9+" : unread}</span>}
    </button>
    {open && <div ref={dialogRef} className={`vb-chat-overlay ${mobile ? "is-mobile" : ""}`} role="dialog" aria-modal="true" aria-label="Chat inbox">
      <button className="vb-chat-overlay__scrim" aria-label="Close chat" onClick={() => setOpen(false)} />
      <ChatView compact={!mobile} embeddedMobile={mobile} onClose={() => setOpen(false)} />
    </div>}
  </>;
}

export default function ChatInbox() {
  const mobile = useIsMobile();
  const [searchParams] = useSearchParams();
  const conversationId = searchParams.get("conversation");
  return <div className="vb-chat-page"><div className="vb-chat-page__heading"><div><span>TEAM COMMUNICATION</span><h1>Chat</h1><p>Private messages and one shared room for quick coordination.</p></div></div><ChatView mobilePage={mobile} initialConversationId={conversationId} /></div>;
}